import React, { useMemo, useState } from 'react';
import { Card, Row, Col, Typography, Drawer, Form, Select, DatePicker, Input, Space, message } from 'antd';
import { TeamOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchSqlData, insertData, updateData } from '../../../services/api';
import StandardTable from '../../../components/common/StandardTable';
import FilterBar from '../../../components/common/FilterBar';
import type { AdmVehicle, Personnel } from '../../../types';
import dayjs from 'dayjs';
import { syncVehicleAssignmentStatus } from '../vehicles/vehicleStatusSync';
import { useTranslation } from 'react-i18next';
import { useGetPersonnel } from '../../../hooks/genericHooks';
import Button from '../../../components/common/Button';

const { Title } = Typography;

interface Assignment {
  id: number;
  vehicle_id: number;
  user_id: number;
  assigned_at: string;
  released_at?: string | null;
  note?: string | null;
}

const MachAssignmentsList: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const invalidateVehicleQueries = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: ['adm_vehicle_assignments_all'] }),
    queryClient.invalidateQueries({ queryKey: ['adm_vehicles_for_assign'] }),
    queryClient.invalidateQueries({ queryKey: ['machineryVehicles'] }),
    queryClient.invalidateQueries({ queryKey: ['machineryVehicleDetail'] }),
    queryClient.invalidateQueries({ queryKey: ['vehicleDetail'] }),
  ]);
  const { data: rows = [], isLoading } = useQuery<Assignment[], Error>({
    queryKey: ['adm_vehicle_assignments_all'],
    queryFn: () => fetchSqlData('adm_vehicle_assignments'),
    staleTime: 60_000,
  });
  const { data: vehicles = [] } = useQuery<AdmVehicle[], Error>({
    queryKey: ['adm_vehicles_for_assign'],
    queryFn: () => fetchSqlData('adm_vehicles'),
    staleTime: 5 * 60_000,
  });
  const { data: personnelResp } = useGetPersonnel();
  const users = useMemo<Personnel[]>(() => {
    const arr = Array.isArray(personnelResp) ? personnelResp : (personnelResp as any)?.data || [];
    return arr as Personnel[];
  }, [personnelResp]);

  const vehicleOptions = (vehicles || []).map(v => ({ value: v.id, label: v.license_plate || String(v.id) }));
  const userOptions = (users || []).map(u => ({ value: u.id, label: `${u.first_name || ''} ${u.last_name || ''}`.trim() }));

  const vehiclesMap = useMemo(() => {
    const m: Record<number, string> = {};
    (vehicles || []).forEach(v => { if (v.id) m[v.id] = v.license_plate || String(v.id); });
    return m;
  }, [vehicles]);
  const usersMap = useMemo(() => {
    const m: Record<number, string> = {};
    (users || []).forEach(u => { if (u.id) m[u.id] = `${u.first_name || ''} ${u.last_name || ''}`.trim(); });
    return m;
  }, [users]);

  const [vehicleFilter, setVehicleFilter] = useState<number | undefined>(undefined);
  const [userFilter, setUserFilter] = useState<number | undefined>(undefined);
  const filtered = useMemo(() => {
    let arr = rows;
    if (vehicleFilter != null) arr = arr.filter(r => Number(r.vehicle_id) === Number(vehicleFilter));
    if (userFilter != null) arr = arr.filter(r => Number(r.user_id) === Number(userFilter));
    return arr;
  }, [rows, vehicleFilter, userFilter]);

  const columns = [
    { title: t('machinery.assignments.list.vehicle'), dataIndex: 'vehicle_id', key: 'vehicle_id', render: (id: number) => vehiclesMap[id] || id, width: 160 },
    { title: t('machinery.assignments.list.user'), dataIndex: 'user_id', key: 'user_id', render: (id: number) => usersMap[id] || id, width: 200 },
    { title: t('machinery.assignments.list.start'), dataIndex: 'assigned_at', key: 'assigned_at', render: (d: string) => d ? new Date(d).toLocaleString() : '-', width: 180 },
    { title: t('machinery.assignments.list.end'), dataIndex: 'released_at', key: 'released_at', render: (d?: string | null) => d ? new Date(d).toLocaleString() : '-', width: 180 },
    { title: t('machinery.assignments.list.note'), dataIndex: 'note', key: 'note' },
  ];

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Assignment | null>(null);
  const [form] = Form.useForm();

  const handleAdd = () => {
    setEditing(null);
    form.resetFields();
    setOpen(true);
  };
  const handleEdit = (r: Assignment) => {
    setEditing(r);
    form.setFieldsValue({
      vehicle_id: r.vehicle_id,
      user_id: r.user_id,
      assigned_at: r.assigned_at ? dayjs(r.assigned_at) : undefined,
      released_at: r.released_at ? dayjs(r.released_at) : undefined,
      note: r.note || undefined,
    });
    setOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const v = await form.validateFields();
      const payload: Partial<Assignment> = {
        vehicle_id: Number(v.vehicle_id),
        user_id: Number(v.user_id),
        assigned_at: v.assigned_at?.toISOString(),
        released_at: v.released_at ? v.released_at.toISOString() : null,
        note: v.note?.trim() || null,
      };

      const vehicle = vehicles.find(item => Number(item.id) === payload.vehicle_id);
      const isActiveAssignment = !payload.released_at;
      const hasAnotherActiveAssignment = rows.some(row =>
        Number(row.vehicle_id) === payload.vehicle_id &&
        !row.released_at &&
        Number(row.id) !== Number(editing?.id)
      );

      if (isActiveAssignment && hasAnotherActiveAssignment) {
        message.warning(t('machinery.assignments.list.activeAssignmentExists'));
        return;
      }

      if (
        isActiveAssignment &&
        vehicle?.status &&
        ['InMaintenance', 'OutOfService', 'Inactive'].includes(vehicle.status)
      ) {
        message.warning(t('machinery.assignments.list.vehicleNotAssignable'));
        return;
      }

      const affectedVehicleIds = new Set<number>();
      if (editing?.vehicle_id) affectedVehicleIds.add(Number(editing.vehicle_id));
      if (payload.vehicle_id) affectedVehicleIds.add(Number(payload.vehicle_id));

      if (editing?.id) {
        await updateData('adm_vehicle_assignments', editing.id, payload);
        message.success(t('machinery.assignments.list.updateSuccess'));
      } else {
        await insertData('adm_vehicle_assignments', payload);
        message.success(t('machinery.assignments.list.addSuccess'));
      }

      await Promise.all(Array.from(affectedVehicleIds).map(vehicleId => syncVehicleAssignmentStatus(vehicleId)));
      setOpen(false);
      setEditing(null);
      await invalidateVehicleQueries();
    } catch (e: any) {
      message.error(e?.message || t('machinery.assignments.list.operationFailed'));
    }
  };

  return (
    <Card>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            <TeamOutlined style={{ marginRight: 8 }} />
            {t('machinery.assignments.list.title')}
          </Title>
        </Col>
        <Col>
          <Button action="add" type="primary" icon={<PlusOutlined />} onClick={handleAdd}>{t('machinery.assignments.list.addButton')}</Button>
        </Col>
      </Row>

      <FilterBar
        items={[
          { type: 'select', key: 'vehicle', placeholder: t('machinery.assignments.list.vehiclePlaceholder'), value: vehicleFilter as any, onChange: v => setVehicleFilter(v), options: vehicleOptions, width: 220, allowClear: true, showSearch: true },
          { type: 'select', key: 'user', placeholder: t('machinery.assignments.list.userPlaceholder'), value: userFilter as any, onChange: v => setUserFilter(v), options: userOptions, width: 220, allowClear: true, showSearch: true },
        ]}
        onReset={() => { setVehicleFilter(undefined); setUserFilter(undefined); }}
      />

      <StandardTable
        rowKey="id"
        columns={[
          ...columns,
          {
            title: 'Actions',
            key: 'actions',
            render: (_: any, r: Assignment) => (
              <Button action="edit" size="small" onClick={() => handleEdit(r)}>Edit</Button>
            ),
            width: 120,
          },
        ] as any}
        dataSource={filtered}
        loading={isLoading}
        bordered
        size="small"
        pagination={{ pageSize: 20, showSizeChanger: true }}
      />

      <Drawer title={editing ? t('machinery.assignments.list.editTitle') : t('machinery.assignments.list.addTitle')} open={open} onClose={() => setOpen(false)} width={520} destroyOnClose>
        <Form layout="vertical" form={form}>
          <Form.Item name="vehicle_id" label={t('machinery.assignments.list.vehicle')} rules={[{ required: true, message: t('machinery.assignments.list.vehicleRequired') }]}>
            <Select options={vehicleOptions} showSearch placeholder={t('machinery.assignments.list.vehicleSelectPlaceholder')} />
          </Form.Item>
          <Form.Item name="user_id" label={t('machinery.assignments.list.user')} rules={[{ required: true, message: t('machinery.assignments.list.userRequired') }]}>
            <Select options={userOptions} showSearch placeholder={t('machinery.assignments.list.userSelectPlaceholder')} />
          </Form.Item>
          <Form.Item name="assigned_at" label={t('machinery.assignments.list.start')} rules={[{ required: true, message: t('machinery.assignments.list.startRequired') }]}>
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="released_at" label={t('machinery.assignments.list.end')}>
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="note" label={t('machinery.assignments.list.note')}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => setOpen(false)}>{t('machinery.assignments.list.cancel')}</Button>
            <Button action={editing ? 'edit' : 'add'} type="primary" onClick={handleSubmit}>{t('machinery.assignments.list.save')}</Button>
          </Space>
        </Form>
      </Drawer>
    </Card>
  );
};

export default MachAssignmentsList;

